import React, { useEffect, useState } from "react";
import { Routes, Route, Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  LayoutDashboard, CalendarDays, BedDouble, Users, Inbox, UserCog,
  LogOut, Plus, CheckCircle2, XCircle, LogIn as LogInIcon, Loader2, Circle,
} from "lucide-react";
import { AuthProvider, useAuth, API_URL } from "./AuthContext";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const CATS = [
  { id: "ac-standard", name: "AC Standard" },
  { id: "ac-deluxe", name: "AC Deluxe" },
  { id: "ac-super-deluxe", name: "AC Super Deluxe" },
  { id: "ac-premium", name: "AC Premium" },
];
const CAT_ROOMS = {
  "ac-standard": [2, 4, 5, 6],
  "ac-deluxe": [3, 7, 8, 10, 15],
  "ac-super-deluxe": [1, 9, 11, 12, 14],
  "ac-premium": [16],
};

/* ---------- Login ---------- */
const Login = () => {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) nav("/admin"); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email, password); nav("/admin"); toast.success("Welcome back"); }
    catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0D120E] text-[#F5F2ED] flex items-center justify-center p-6">
      <form onSubmit={submit} data-testid="login-form" className="w-full max-w-md bg-[#111a13] border border-[#F5F2ED]/10 p-10">
        <p className="overline text-[#B89965]">Hotel Virasat Retreat</p>
        <h1 className="font-display text-4xl mt-3">PMS Login</h1>
        <p className="text-sm text-[#F5F2ED]/60 mt-2">Sign in with your owner or staff account.</p>
        <label className="block mt-8 text-xs text-[#F5F2ED]/60">EMAIL</label>
        <input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
          className="mt-1 w-full bg-transparent border-b border-[#F5F2ED]/30 py-2 focus:outline-none focus:border-[#B89965]" />
        <label className="block mt-6 text-xs text-[#F5F2ED]/60">PASSWORD</label>
        <input data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required
          className="mt-1 w-full bg-transparent border-b border-[#F5F2ED]/30 py-2 focus:outline-none focus:border-[#B89965]" />
        <button data-testid="login-submit" disabled={busy} className="mt-10 w-full bg-[#B89965] text-[#0D120E] rounded-full py-3.5 inline-flex items-center justify-center gap-2 hover:bg-[#c9aa76] disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <LogInIcon size={16} />} Sign In
        </button>
      </form>
    </div>
  );
};

/* ---------- Layout ---------- */
const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const links = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
    { to: "/admin/bookings", label: "Bookings", icon: CalendarDays },
    { to: "/admin/rooms", label: "Rooms", icon: BedDouble },
    { to: "/admin/guests", label: "Guests", icon: Users },
    { to: "/admin/leads", label: "Leads", icon: Inbox },
    ...(user?.role === "owner" ? [{ to: "/admin/users", label: "Users", icon: UserCog }] : []),
  ];
  return (
    <div className="min-h-screen bg-[#F5F2ED] text-[#1A1A1A] flex">
      <aside className="w-60 bg-[#0D120E] text-[#F5F2ED] p-6 sticky top-0 h-screen flex flex-col">
        <Link to="/" className="flex flex-col leading-none mb-10">
          <span className="font-display text-xl">Virasat Retreat</span>
          <span className="overline text-[10px] text-[#B89965] mt-1">PMS · Owner Console</span>
        </Link>
        <nav className="flex flex-col gap-1">
          {links.map(({ to, label, icon: Icon, exact }) => {
            const active = exact ? loc.pathname === to : loc.pathname.startsWith(to);
            return (
              <Link key={to} to={to} data-testid={`nav-${label.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded text-sm transition-colors ${active ? "bg-[#B89965] text-[#0D120E]" : "text-[#F5F2ED]/70 hover:bg-[#F5F2ED]/10 hover:text-[#F5F2ED]"}`}>
                <Icon size={16} /> {label}
              </Link>
            );
          })}
        </nav>
        <div className="mt-auto pt-6 border-t border-[#F5F2ED]/10 text-xs">
          <p className="text-[#F5F2ED]/80">{user?.name}</p>
          <p className="text-[#F5F2ED]/50 uppercase tracking-wider mt-0.5">{user?.role}</p>
          <button data-testid="logout-btn" onClick={() => { logout(); nav("/admin/login"); }} className="mt-3 inline-flex items-center gap-2 text-[#F5F2ED]/70 hover:text-[#B89965]">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 p-8 md:p-10 overflow-x-auto">{children}</main>
    </div>
  );
};

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F5F2ED]"><Loader2 className="animate-spin text-[#2C402E]" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
};

/* ---------- Dashboard ---------- */
const Dashboard = () => {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API_URL}/dashboard`).then(({ data }) => setD(data)); }, []);
  if (!d) return <Loader2 className="animate-spin" />;
  const stats = [
    { label: "Occupancy", value: `${d.occupancy_pct}%` },
    { label: "In-house", value: `${d.in_house}` },
    { label: "Arrivals today", value: `${d.arrivals_today.length}` },
    { label: "Departures today", value: `${d.departures_today.length}` },
    { label: "Revenue MTD", value: inr(d.revenue_mtd) },
    { label: "Open leads", value: `${d.open_leads}` },
  ];
  return (
    <div data-testid="dashboard">
      <h1 className="font-display text-4xl">Today · {d.date}</h1>
      <p className="text-sm text-[#1A1A1A]/60 mt-1">Live snapshot of your hotel.</p>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-white border border-black/10 p-5">
            <p className="overline text-[10px] text-[#1A1A1A]/50">{s.label}</p>
            <p className="font-display text-3xl mt-2 text-[#2C402E]">{s.value}</p>
          </div>
        ))}
      </div>
      <div className="mt-10 grid md:grid-cols-2 gap-6">
        <ArrivalDeparture title="Arrivals today" rows={d.arrivals_today} action="checkin" />
        <ArrivalDeparture title="Departures today" rows={d.departures_today} action="checkout" />
      </div>
    </div>
  );
};

const ArrivalDeparture = ({ title, rows, action }) => {
  const reload = () => window.location.reload();
  const doAction = async (id) => {
    try { await axios.post(`${API_URL}/bookings/${id}/${action}`); toast.success(action === "checkin" ? "Checked in" : "Checked out"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-white border border-black/10">
      <div className="px-5 py-3 border-b border-black/10 flex justify-between items-center">
        <p className="font-display text-lg">{title}</p><span className="text-xs text-[#1A1A1A]/50">{rows.length}</span>
      </div>
      {rows.length === 0 ? <p className="p-5 text-sm text-[#1A1A1A]/50">None</p> : (
        <ul className="divide-y divide-black/10">
          {rows.map((r) => (
            <li key={r.id} className="px-5 py-3 flex justify-between items-center text-sm">
              <div><p className="font-medium">{r.guest_name}</p><p className="text-xs text-[#1A1A1A]/60">Room {r.room_number} · {inr(r.total)}</p></div>
              <button data-testid={`${action}-${r.id}`} onClick={() => doAction(r.id)} className="text-[#2C402E] hover:text-[#B89965] inline-flex items-center gap-1 text-xs uppercase tracking-wider">
                {action === "checkin" ? "Check-in" : "Check-out"} <CheckCircle2 size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ---------- Bookings ---------- */
const NewBookingModal = ({ onClose, onCreated }) => {
  const [f, setF] = useState({
    guest_name: "", guest_phone: "", guest_email: "",
    category: "ac-standard", room_number: 2,
    check_in: new Date().toISOString().slice(0, 10),
    check_out: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
    meal_plan: "ep", adults: 1, children: 0, extra_bed: 0, notes: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API_URL}/bookings`, {
        ...f, room_number: parseInt(f.room_number, 10), adults: parseInt(f.adults, 10),
        children: parseInt(f.children, 10), extra_bed: parseInt(f.extra_bed, 10),
      });
      toast.success("Booking created"); onCreated(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create booking"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} data-testid="new-booking-form" onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl p-8 max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-center"><h2 className="font-display text-2xl">New Booking</h2><button type="button" onClick={onClose} className="text-[#1A1A1A]/60 hover:text-[#1A1A1A]"><XCircle size={20} /></button></div>
        <div className="mt-6 grid grid-cols-2 gap-4">
          <Field label="Guest name" tid="bk-name"><input required data-testid="bk-name" className="input" value={f.guest_name} onChange={set("guest_name")} /></Field>
          <Field label="Phone" tid="bk-phone"><input required data-testid="bk-phone" className="input" value={f.guest_phone} onChange={set("guest_phone")} /></Field>
          <Field label="Email" tid="bk-email"><input type="email" data-testid="bk-email" className="input" value={f.guest_email} onChange={set("guest_email")} /></Field>
          <Field label="Category">
            <select data-testid="bk-category" className="input" value={f.category} onChange={(e) => { setF({ ...f, category: e.target.value, room_number: CAT_ROOMS[e.target.value][0] }); }}>
              {CATS.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Room #"><select data-testid="bk-room" className="input" value={f.room_number} onChange={set("room_number")}>{CAT_ROOMS[f.category].map((n) => <option key={n} value={n}>{n}</option>)}</select></Field>
          <Field label="Meal plan"><select data-testid="bk-plan" className="input" value={f.meal_plan} onChange={set("meal_plan")}><option value="ep">EP (Room only)</option><option value="cp">CP (Breakfast)</option><option value="map">MAP</option><option value="ap">AP</option></select></Field>
          <Field label="Check-in"><input type="date" required data-testid="bk-checkin" className="input" value={f.check_in} onChange={set("check_in")} /></Field>
          <Field label="Check-out"><input type="date" required data-testid="bk-checkout" className="input" value={f.check_out} onChange={set("check_out")} /></Field>
          <Field label="Adults"><input type="number" min="1" data-testid="bk-adults" className="input" value={f.adults} onChange={set("adults")} /></Field>
          <Field label="Children"><input type="number" min="0" data-testid="bk-children" className="input" value={f.children} onChange={set("children")} /></Field>
          <Field label="Extra bed"><input type="number" min="0" data-testid="bk-extra" className="input" value={f.extra_bed} onChange={set("extra_bed")} /></Field>
          <Field label="Notes" full><textarea data-testid="bk-notes" className="input" rows={2} value={f.notes} onChange={set("notes")} /></Field>
        </div>
        <button disabled={busy} data-testid="bk-submit" className="mt-8 w-full bg-[#2C402E] text-[#F5F2ED] rounded-full py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Create booking
        </button>
      </form>
    </div>
  );
};

const Field = ({ label, children, full }) => (
  <label className={`block ${full ? "col-span-2" : ""}`}>
    <span className="overline text-[10px] text-[#1A1A1A]/50">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const Bookings = () => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => axios.get(`${API_URL}/bookings`).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const act = async (id, verb) => {
    try { await axios.post(`${API_URL}/bookings/${id}/${verb}`); toast.success(verb); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const badge = { booked: "bg-blue-100 text-blue-800", checked_in: "bg-green-100 text-green-800", checked_out: "bg-gray-100 text-gray-700", cancelled: "bg-red-100 text-red-800" };
  return (
    <div data-testid="bookings-page">
      <div className="flex justify-between items-center">
        <h1 className="font-display text-4xl">Bookings</h1>
        <button data-testid="new-booking-btn" onClick={() => setOpen(true)} className="bg-[#2C402E] text-[#F5F2ED] rounded-full px-6 py-2.5 text-sm inline-flex items-center gap-2"><Plus size={15} /> New booking</button>
      </div>
      {open && <NewBookingModal onClose={() => setOpen(false)} onCreated={load} />}
      <div className="mt-8 bg-white border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#EBE7DF] text-left text-xs uppercase tracking-wider text-[#1A1A1A]/60">
            <tr><th className="p-3">Guest</th><th className="p-3">Room</th><th className="p-3">Check-in</th><th className="p-3">Check-out</th><th className="p-3">Plan</th><th className="p-3">Total</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id} className="border-t border-black/5">
                <td className="p-3"><p className="font-medium">{b.guest_name}</p><p className="text-xs text-[#1A1A1A]/50">{b.guest_phone}</p></td>
                <td className="p-3">#{b.room_number}<p className="text-xs text-[#1A1A1A]/50">{b.category}</p></td>
                <td className="p-3">{b.check_in}</td><td className="p-3">{b.check_out}</td>
                <td className="p-3 uppercase text-xs">{b.meal_plan}</td>
                <td className="p-3">{inr(b.total)}</td>
                <td className="p-3"><span className={`text-[11px] px-2 py-0.5 rounded-full ${badge[b.status] || ""}`}>{b.status}</span></td>
                <td className="p-3 space-x-2 whitespace-nowrap">
                  {b.status === "booked" && <><button onClick={() => act(b.id, "checkin")} data-testid={`ci-${b.id}`} className="text-[#2C402E] text-xs">Check-in</button><button onClick={() => act(b.id, "cancel")} className="text-red-600 text-xs">Cancel</button></>}
                  {b.status === "checked_in" && <button onClick={() => act(b.id, "checkout")} data-testid={`co-${b.id}`} className="text-[#2C402E] text-xs">Check-out</button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-[#1A1A1A]/50">No bookings yet — create your first one.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ---------- Rooms ---------- */
const Rooms = () => {
  const [rooms, setRooms] = useState([]);
  const load = () => axios.get(`${API_URL}/rooms`).then(({ data }) => setRooms(data));
  useEffect(() => { load(); }, []);
  const setHK = async (n, status) => {
    try { await axios.patch(`${API_URL}/rooms/${n}/housekeeping?status=${status}`); toast.success("Updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const hkColor = { clean: "text-green-700 bg-green-100", dirty: "text-orange-700 bg-orange-100", out_of_service: "text-red-700 bg-red-100" };
  return (
    <div data-testid="rooms-page">
      <h1 className="font-display text-4xl">Rooms</h1>
      <p className="text-sm text-[#1A1A1A]/60 mt-1">15 rooms · 4 categories</p>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {rooms.map((r) => (
          <div key={r.number} data-testid={`room-${r.number}`} className="bg-white border border-black/10 p-4">
            <div className="flex justify-between"><p className="font-display text-3xl">{r.number}</p><span className={`text-[10px] px-2 py-0.5 rounded-full h-fit ${hkColor[r.housekeeping]}`}>{r.housekeeping.replace("_", " ")}</span></div>
            <p className="text-xs text-[#1A1A1A]/60 mt-1">{r.category_name}</p>
            <div className="mt-3 flex gap-1 text-[10px]">
              {["clean", "dirty", "out_of_service"].map((s) => (
                <button key={s} onClick={() => setHK(r.number, s)} className={`px-2 py-1 rounded border ${r.housekeeping === s ? "bg-[#2C402E] text-[#F5F2ED] border-[#2C402E]" : "border-black/15 hover:bg-black/5"}`}>{s === "out_of_service" ? "OOS" : s}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- Guests / Leads ---------- */
const Guests = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { axios.get(`${API_URL}/guests`).then(({ data }) => setRows(data)); }, []);
  return (
    <div data-testid="guests-page">
      <h1 className="font-display text-4xl">Guests</h1>
      <div className="mt-8 bg-white border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#EBE7DF] text-left text-xs uppercase tracking-wider text-[#1A1A1A]/60"><tr><th className="p-3">Name</th><th className="p-3">Phone</th><th className="p-3">Email</th><th className="p-3">Stays</th><th className="p-3">Last stay</th></tr></thead>
          <tbody>
            {rows.map((g) => (<tr key={g.id} className="border-t border-black/5"><td className="p-3 font-medium">{g.name}</td><td className="p-3">{g.phone}</td><td className="p-3">{g.email || "—"}</td><td className="p-3">{g.stays_count}</td><td className="p-3">{g.last_stay || "—"}</td></tr>))}
            {rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#1A1A1A]/50">No guests yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Leads = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { axios.get(`${API_URL}/leads`).then(({ data }) => setRows(data)); }, []);
  return (
    <div data-testid="leads-page">
      <h1 className="font-display text-4xl">Leads · Website Enquiries</h1>
      <div className="mt-8 bg-white border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#EBE7DF] text-left text-xs uppercase tracking-wider text-[#1A1A1A]/60"><tr><th className="p-3">When</th><th className="p-3">Name</th><th className="p-3">Phone</th><th className="p-3">Email</th><th className="p-3">Type</th><th className="p-3">Dates</th><th className="p-3">Message</th></tr></thead>
          <tbody>
            {rows.map((l) => (<tr key={l.id} className="border-t border-black/5"><td className="p-3 text-xs">{new Date(l.created_at).toLocaleString()}</td><td className="p-3 font-medium">{l.name}</td><td className="p-3">{l.phone}</td><td className="p-3">{l.email}</td><td className="p-3 uppercase text-xs">{l.enquiry_type}</td><td className="p-3 text-xs">{l.check_in} → {l.check_out}</td><td className="p-3 text-xs">{l.message || "—"}</td></tr>))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#1A1A1A]/50">No leads yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ---------- Users (owner only) ---------- */
const Users2 = () => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ email: "", password: "", name: "", role: "staff" });
  const load = () => axios.get(`${API_URL}/auth/users`).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API_URL}/auth/users`, f); toast.success("Staff added"); setF({ email: "", password: "", name: "", role: "staff" }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="users-page">
      <h1 className="font-display text-4xl">Users</h1>
      <form onSubmit={submit} data-testid="new-staff-form" className="mt-6 bg-white border border-black/10 p-6 grid md:grid-cols-5 gap-3 items-end">
        <Field label="Name"><input required className="input" data-testid="staff-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Email"><input required type="email" className="input" data-testid="staff-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Password"><input required type="password" className="input" data-testid="staff-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></Field>
        <Field label="Role"><select className="input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}><option value="staff">Staff</option><option value="owner">Owner</option></select></Field>
        <button data-testid="staff-submit" className="bg-[#2C402E] text-[#F5F2ED] rounded-full py-2.5 inline-flex items-center justify-center gap-2"><Plus size={15} /> Add</button>
      </form>
      <div className="mt-8 bg-white border border-black/10">
        <table className="w-full text-sm">
          <thead className="bg-[#EBE7DF] text-left text-xs uppercase tracking-wider text-[#1A1A1A]/60"><tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th></tr></thead>
          <tbody>{rows.map((u) => (<tr key={u.id} className="border-t border-black/5"><td className="p-3 font-medium">{u.name}</td><td className="p-3">{u.email}</td><td className="p-3 uppercase text-xs"><Circle size={8} className="inline mr-1" />{u.role}</td></tr>))}</tbody>
        </table>
      </div>
    </div>
  );
};

/* ---------- Root ---------- */
export default function AdminApp() {
  return (
    <AuthProvider>
      <style>{`.input{width:100%;background:transparent;border:0;border-bottom:1px solid rgba(0,0,0,0.15);padding:6px 0;font-size:14px;} .input:focus{outline:none;border-bottom-color:#B89965;}`}</style>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<Protected><Layout><Dashboard /></Layout></Protected>} />
        <Route path="/bookings" element={<Protected><Layout><Bookings /></Layout></Protected>} />
        <Route path="/rooms" element={<Protected><Layout><Rooms /></Layout></Protected>} />
        <Route path="/guests" element={<Protected><Layout><Guests /></Layout></Protected>} />
        <Route path="/leads" element={<Protected><Layout><Leads /></Layout></Protected>} />
        <Route path="/users" element={<Protected><Layout><Users2 /></Layout></Protected>} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AuthProvider>
  );
}
